import React, { useState, useEffect } from "react"
import { getJson } from "../../helpers/http"
import { API_ENDPOINTS } from "../../helpers/api"
import { pluralizeDays } from "../../helpers/auth"
import Modal from "./Modal"

export const FEATURES = [
  { key: "smart", name: "Smart AI with web search", desc: "Get informed suggestions backed by real-time web results" },
  {
    key: "ai_planning",
    name: "AI planning",
    desc: "Describe what you want and get a personalized plan",
  },
  { key: "location", name: "Precise location", desc: "Use your precise location for plans and routes" },
  {
    key: "route",
    name: "Route optimization",
    desc: "Fastest route based on wait times and location",
  },
  {
    key: "wait_charts",
    name: "Wait time predictions + history",
    desc: "Current, predicted, and historical wait times per attraction",
  },
  {
    key: "crowd_calendar",
    name: "Crowd calendar forecasts",
    desc: "See how busy each day is for the next week, plus any day's predicted wait times",
  },
  {
    key: "requests",
    name: "25 AI requests per day",
    desc: "Plenty for even your most indecisive days",
  },
  {
    key: "smart_requests",
    name: "25 smart AI requests per day",
    desc: "Plenty for even your most indecisive days",
  },
]

const PLAN = { name: "Weekly Pass", price: "$0.99", duration: "7 days" }

const StatusRow = ({ label, value }) => (
  <div className="premium-pass-status-row">
    <span className="premium-pass-status-label">{label}</span>
    <span className="premium-pass-status-value">{value}</span>
  </div>
)

const FeaturesList = ({ features = FEATURES }) => (
  <div className="premium-pass-features">
    <p className="premium-pass-features-title">Premium includes:</p>
    <ul className="premium-pass-features-list">
      {features.map((feature) => (
        <li key={feature.key}>
          <strong>{feature.name}</strong>
          <span className="premium-pass-feature-desc">{feature.desc}</span>
        </li>
      ))}
    </ul>
  </div>
)

const formatExpiry = (isoString) => {
  const date = new Date(isoString)
  const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZoneName: "short" })
  return `${dateStr} at ${timeStr}`
}

const PremiumPassModal = ({ onClose, purchaseSuccess = false, features = FEATURES, crossSiteNote = null }) => {
  const [premiumPass, setPremiumPass] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchPremiumPass = async () => {
      const { data, error: apiError } = await getJson(API_ENDPOINTS.PREMIUM_PASS)
      setLoading(false)
      if (apiError) {
        setError(apiError)
        return
      }
      setPremiumPass(data)
    }
    fetchPremiumPass()
  }, [])

  const handleBuy = () => {
    if (!premiumPass) return
    window.location.href = premiumPass.weekly_pass_url
  }

  const renderSuccess = () => {
    const days = premiumPass?.days_remaining
    return (
      <div className="premium-pass-success">
        <div className="premium-pass-success-icon">&#10003;</div>
        <p className="premium-pass-success-text">You're all set!</p>
        {premiumPass && (
          <div className="premium-pass-success-details">
            <StatusRow label="Purchased" value={PLAN.name} />
            {days != null && <StatusRow label="Total access" value={pluralizeDays(days)} />}
            {premiumPass.expires_at && <StatusRow label="Expires" value={formatExpiry(premiumPass.expires_at)} />}
          </div>
        )}
      </div>
    )
  }

  const renderStatus = () => (
    <div className="premium-pass-active">
      {premiumPass.active && (
        <>
          <StatusRow label="Time remaining" value={pluralizeDays(premiumPass.days_remaining)} />
          <StatusRow label="Expires" value={formatExpiry(premiumPass.expires_at)} />
          <StatusRow
            label="Credits remaining today"
            value={`${premiumPass.premium_requests_remaining}/${premiumPass.premium_requests_limit}`}
          />
        </>
      )}
      {premiumPass.premium_credits_remaining > 0 && (
        <StatusRow label="Credits remaining" value={premiumPass.premium_credits_remaining} />
      )}
    </div>
  )

  const renderContent = () => {
    if (loading) return <div className="modal-loading">Loading...</div>
    if (error) return <div className="error-message modal-error">{error}</div>
    if (purchaseSuccess) return renderSuccess()
    return (
      <>
        {(premiumPass?.active || premiumPass?.premium_credits_remaining > 0) && renderStatus()}
        <FeaturesList features={features} />
        {premiumPass?.active && (
          <p className="premium-pass-extend-hint">
            Purchase another {PLAN.name} to extend your access by {PLAN.duration}.
          </p>
        )}
        <button className="premium-pass-plan-card" onClick={handleBuy}>
          <div className="premium-pass-plan-name">{PLAN.name}</div>
          <div className="premium-pass-plan-price">{PLAN.price}</div>
          <div className="premium-pass-plan-duration">{PLAN.duration}</div>
        </button>
        {crossSiteNote && <p className="premium-pass-cross-site-note">{crossSiteNote}</p>}
      </>
    )
  }

  const footer = (
    <button className={`btn-base ${purchaseSuccess ? "btn-primary" : "btn-secondary"} modal-btn`} onClick={onClose}>
      {purchaseSuccess ? "Done" : "Close"}
    </button>
  )

  return (
    <Modal title="Premium" onClose={onClose} footer={footer}>
      {renderContent()}
    </Modal>
  )
}

export default PremiumPassModal
